import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

const TEAM = [
  { initials: "PS", name: "DWC Patient Specialists", role: "Trained cannabis wellness consultants" },
  { initials: "DL", name: "DWC Delivery Team", role: "Same-day medical cannabis delivery, Washington DC" },
  { initials: "OW", name: "DWC Ownership", role: "Growth and Wellness, LLC · DC ABCA License ABCA-127954" },
];

export default function Team() {
  return (
    <>
      <SEO
        title="Our Team · District Wellness Center"
        description="Meet the people behind District Wellness Center — trained patient specialists at our Washington DC dispensary on Georgia Ave."
        path="/team"
        breadcrumbs={[{ name: "Team", path: "/team" }]}
      />
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24 sm:py-32">
          <div className="max-w-3xl">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">Our Team</div>
            <h1 className="font-serif text-5xl sm:text-7xl leading-[0.95] tracking-tight">
              Real <em className="text-brand">people</em>, real answers.
            </h1>
            <p className="mt-6 text-ink-muted text-lg leading-relaxed">
              DWC is staffed by trained patient specialists who live in the neighborhoods we serve. They'll match
              you with product — never push it.
            </p>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {TEAM.map((m) => (
              <article key={m.name} className="p-10 border border-black/10 hover:border-brand transition-colors" data-testid={`team-${m.initials}`}>
                <div className="w-20 h-20 bg-ink text-white font-serif text-3xl flex items-center justify-center mb-8">
                  {m.initials}
                </div>
                <h3 className="font-serif text-2xl leading-tight">{m.name}</h3>
                <p className="text-sm text-ink-muted mt-2">{m.role}</p>
              </article>
            ))}
          </div>

          <div className="mt-20 p-10 bg-[#FAFAFA] border border-black/10 grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-3">Careers</div>
              <h2 className="font-serif text-4xl">Work at DWC.</h2>
            </div>
            <p className="text-ink-muted">
              We're always looking for great people who love cannabis, wellness, and the District. Email
              <a href="mailto:info@districtwellnesscenter.com" className="text-ink link-underline ml-1">info@districtwellnesscenter.com</a> with a note about yourself.
            </p>
          </div>
        </div>
      </section>

      {/* LONG-FORM: who works at DWC + why it matters */}
      <section className="py-24 bg-[#FAFAFA] border-t border-black/10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">Meet the DWC team</div>
          <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight">
            A neighborhood dispensary run by people who live here.
          </h2>
          <div className="mt-8 space-y-5 text-ink-muted text-lg leading-relaxed">
            <p>
              District Wellness Center is a licensed DC medical cannabis dispensary on Georgia Ave NW —
              staffed by patient specialists who grew up in, or moved to, Petworth, Columbia Heights, Shaw,
              Brightwood, Park View and the rest of upper Northwest DC. We&rsquo;re not a national chain
              dropping into the District. We&rsquo;re your neighbors.
            </p>
            <p>
              Every specialist behind the counter at 4915 Georgia Ave NW is trained in DC OCA compliance,
              product formats (flower, pre-rolls, vapes, edibles, tinctures, concentrates), dosing, and
              terpene profiles. If you&rsquo;re new to medical cannabis, we&rsquo;ll take our time.
              You&rsquo;re not just a transaction.
            </p>
          </div>

          <h3 className="font-serif text-2xl text-ink mt-12 mb-4">Trained patient specialists</h3>
          <p className="text-lg text-ink-muted leading-relaxed">
            Our team can walk you through the differences between indica-leaning hybrids like Khalifa Kush
            and gassier drops from Alt Sol, help you find the right THC-to-CBD ratio for sleep or focus, and
            explain how edibles are dosed. Ask us anything — including which brand tastes the way you
            describe.
          </p>

          <h3 className="font-serif text-2xl text-ink mt-12 mb-4">The DWC delivery team</h3>
          <p className="text-lg text-ink-muted leading-relaxed">
            Our drivers cover the entire District — Georgetown, Dupont Circle, Adams Morgan, Capitol Hill,
            Navy Yard, and every neighborhood in between. Same-day windows are usually under an hour on
            weeknights. We arrive in plain vehicles with plain, child-resistant packaging.
          </p>

          <h3 className="font-serif text-2xl text-ink mt-12 mb-4">Ownership & licensing</h3>
          <p className="text-lg text-ink-muted leading-relaxed">
            DWC operates under Growth and Wellness, LLC (T/A District Wellness Center), licensed by the DC
            Alcoholic Beverage &amp; Cannabis Administration (ABCA-127954). We&rsquo;re locally owned and
            we reinvest into the DC community every year.
          </p>

          {/* Related links */}
          <div className="mt-16 pt-10 border-t border-black/10 grid sm:grid-cols-3 gap-6">
            <Link to="/about" className="group block" data-testid="team-related-about">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-2">Related</div>
              <div className="font-serif text-xl text-ink group-hover:text-brand">About DWC →</div>
            </Link>
            <Link to="/locations" className="group block" data-testid="team-related-locations">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-2">Related</div>
              <div className="font-serif text-xl text-ink group-hover:text-brand">Visit us in DC →</div>
            </Link>
            <Link to="/blog/visiting-district-wellness-center-dc" className="group block" data-testid="team-related-visit">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-2">Related</div>
              <div className="font-serif text-xl text-ink group-hover:text-brand">First-time visit guide →</div>
            </Link>
          </div>
        </div>
      </section>

      {/* Google review CTA */}
      <section className="bg-ink text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05]">
            Loved your visit? <em className="text-brand">Tell Google.</em>
          </h2>
          <p className="mt-5 text-white/70 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            Your honest review helps other DC patients find us and helps our local business grow.
          </p>
          <div className="mt-10">
            <a
              href="https://maps.app.goo.gl/ynJcSrirV4qpH1gz8"
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em]"
              data-testid="team-google-review"
            >
              Leave a Google review →
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
